import { Link, useParams } from 'react-router';
import Alert from '~/components/Alert';
import Button from '~/components/button/Button';
import LinkButton from '~/components/button/LinkButton';
import CardSection from '~/components/CardSection';
import Disclosure from '~/components/Disclosure';
import Divide from '~/components/Divide';
import Icon from '~/components/Icon';
import Page from '~/components/layout/page/Page';
import { ClaimRollupStatuses, CORSO_BOX_ICON_IMG_URL } from '~/utils/constants';
import { formatDate } from '~/utils/formatters';
import { useLoaderData } from './orderHub';
import Tracking from './Tracking';

type OrderHubLoaderData = ReturnType<typeof useLoaderData>;
// ----------- components ----------------------

// different states for tracking, for an order that has not been fulfilled
function NoTrackingData({ isOrderFulfilled }: { isOrderFulfilled: boolean }) {
  return isOrderFulfilled ? (
    <div
      data-testid="no-tracking-info"
      className="flex items-center gap-2 border-none label-lg text-on-surface"
    >
      <Icon icon="InfoCircle" className="size-6 shrink-0" />
      Tracking information not available.
    </div>
  ) : (
    <div
      data-testid="order-placed"
      className="flex items-center gap-2 border-none label-lg text-on-surface"
    >
      <div className="text-on-surface">
        <p className="label-md">TRACKING INFORMATION</p>
        <p className="heading-lg">Order Placed</p>
        <p className="text-on-surface-variant">Getting Ready To Ship</p>
      </div>
    </div>
  );
}

function TrackingInfo({
  trackers,
  isOrderFulfilled,
}: {
  trackers: OrderHubLoaderData['tracking']['trackers'];
  isOrderFulfilled: boolean;
}) {
  return (
    <Page.Section>
      {trackers.length ? (
        <div className="flex flex-col gap-4">
          <Tracking trackers={trackers} />
        </div>
      ) : (
        <NoTrackingData isOrderFulfilled={isOrderFulfilled} />
      )}
    </Page.Section>
  );
}

function OrderEdit({
  isEditable,
  description,
}: {
  isEditable: boolean;
  description: string;
}) {
  const params = useParams();

  const action = isEditable ? (
    <LinkButton
      relative="path"
      to={`/${params.store}/order/${params.idFromPlatform}/edit`}
      className="w-full shrink-0 md:w-60"
    >
      Edit Order
    </LinkButton>
  ) : null;

  return (
    <Page.Section>
      <CardSection
        icon="PenToSquare"
        title="Edit Order"
        description={description}
        action={action}
      />
    </Page.Section>
  );
}

function OrderClaims({
  claims,
}: {
  claims: NonNullable<OrderHubLoaderData['startClaim']['existingClaims']>;
}) {
  return (
    <Disclosure
      summary={
        <span className="flex items-center gap-2">
          <Icon icon="InfoCircle" className="size-6 shrink-0" />
          This order has existing requests
        </span>
      }
    >
      <Divide
        as="ul"
        ariaLabel="Existing Claim Requests"
        className="pt-2 *:py-2"
      >
        {claims.map((claim) => (
          <li
            key={claim.externalId}
            className="relative grid w-full grid-cols-[auto_auto_20px] grid-rows-2 gap-x-2"
          >
            <span className="label-lg text-on-surface">{claim.externalId}</span>
            <span className="col-start-1 row-start-2 body-sm text-on-surface-variant">
              {claim.claimType}
            </span>

            <span className="text-right label-lg text-on-surface">
              {formatDate(claim.createdOn)}
            </span>
            <span className="col-start-2 row-start-2 text-right body-sm text-on-surface-variant">
              {ClaimRollupStatuses[claim.claimRollup.code]}
            </span>

            <Icon
              icon="ChevronRight"
              className="col-start-3 row-span-2 size-5 self-center text-on-surface-variant"
            />

            <Link
              className="absolute inset-x-0 -top-px bottom-0"
              to={`../claim/overview/${claim.externalId}`}
              aria-label={`Go to claim ${claim.externalId}`}
            />
          </li>
        ))}
      </Divide>
    </Disclosure>
  );
}

function ShippingProtectionHelp({
  wasShippingProtected,
}: {
  wasShippingProtected: boolean;
}) {
  const params = useParams();

  const description = wasShippingProtected
    ? 'Your order has shipping protection. Report a shipping issue to request a replacement.'
    : 'Report a shipping issue to get help.';

  return (
    <CardSection
      media={
        <img
          className="size-14 shrink-0"
          alt="Green Shipping Protection"
          src={CORSO_BOX_ICON_IMG_URL}
          aria-hidden
        />
      }
      title="Shipping Issues"
      description={description}
      action={
        <LinkButton
          relative="path"
          to={`/${params.store}/reorder/${params.idFromPlatform}`}
        >
          Report Shipping Issue
        </LinkButton>
      }
    />
  );
}

function StartCrewClaim({
  title,
  description,
  isOrderFulfilled,
  pendingClaimType,
}: {
  title: string;
  description: string;
  isOrderFulfilled: boolean;
  pendingClaimType?: string;
}) {
  return (
    <CardSection
      icon="ArrowRightArrowLeft"
      title={title}
      description={description}
      action={
        <div className="flex w-full flex-col gap-2">
          {isOrderFulfilled ? (
            <LinkButton variant="filled" to="new-request">
              Start New Request
            </LinkButton>
          ) : (
            <Button disabled>Start New Request</Button>
          )}
          {pendingClaimType && (
            <LinkButton variant="ghost" to={pendingClaimType.toLowerCase()}>
              Resume Request
            </LinkButton>
          )}
        </div>
      }
    />
  );
}

function StartRightToWithdrawClaim({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <CardSection
      icon="Ban"
      title={title}
      description={description}
      action={
        <LinkButton variant="filled" to="withdraw">
          Withdraw From Contract
        </LinkButton>
      }
    />
  );
}

function StartClaim({
  startClaimData,
  isOrderFulfilled,
}: {
  startClaimData: OrderHubLoaderData['startClaim'];
  isOrderFulfilled: boolean;
}) {
  const {
    existingClaims,
    showStartShippingProtectionClaim,
    wasShippingProtected,
    showStartCrewClaim,
    pendingClaimType,
    headline,
    description,
    rightToWithdraw,
  } = startClaimData;

  return (
    <Page.Section>
      <Divide>
        {existingClaims && <OrderClaims claims={existingClaims} />}

        {rightToWithdraw && !existingClaims?.length && (
          <StartRightToWithdrawClaim
            title={rightToWithdraw.rightToWithdrawTitle}
            description={rightToWithdraw.rightToWithdrawDescription}
          />
        )}

        {showStartShippingProtectionClaim && (
          <ShippingProtectionHelp wasShippingProtected={wasShippingProtected} />
        )}
        {showStartCrewClaim && (
          <StartCrewClaim
            isOrderFulfilled={isOrderFulfilled}
            pendingClaimType={pendingClaimType}
            title={headline}
            description={description}
          />
        )}
      </Divide>
    </Page.Section>
  );
}

export default function OrderHubPage() {
  const { tracking, isOrderFulfilled, startClaim, canceledOn, orderEdit } =
    useLoaderData();

  return (
    <Page.Root title="Order Overview" hideHeader>
      {canceledOn && (
        <Alert title="Order Canceled" variant="info">
          This order was canceled on {formatDate(canceledOn)}.
        </Alert>
      )}
      {!canceledOn && tracking.show && (
        <TrackingInfo
          trackers={tracking.trackers}
          isOrderFulfilled={isOrderFulfilled}
        />
      )}

      {orderEdit.show && (
        <OrderEdit
          isEditable={orderEdit.isEditable}
          description={orderEdit.description}
        />
      )}

      {startClaim.show && (
        <StartClaim
          startClaimData={startClaim}
          isOrderFulfilled={isOrderFulfilled}
        />
      )}
    </Page.Root>
  );
}
